const ARTICLE_SELECTOR = '[data-koda-article]';
const HEADING_SELECTOR = 'h2, h3';
const MIN_TOC_ITEMS = 3;
const FAQ_PATTERN = /\b(faq|frequently asked questions|questions)\b/i;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const slugify = (value) => {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || 'section';
};

const uniqueId = (base, usedIds) => {
  let id = base;
  let index = 2;

  while (usedIds.has(id) || document.getElementById(id)) {
    id = `${base}-${index}`;
    index += 1;
  }

  usedIds.add(id);
  return id;
};

const buildTocLink = (heading) => {
  const link = document.createElement('a');
  link.href = `#${heading.id}`;
  link.textContent = heading.textContent.trim();
  link.dataset.level = heading.tagName;

  link.addEventListener('click', (event) => {
    event.preventDefault();
    heading.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    history.replaceState(null, '', `#${heading.id}`);
    link.focus({ preventScroll: true });
  });

  return link;
};

const getTocHeadings = (content) => {
  let insideFaq = false;

  return [...content.querySelectorAll(HEADING_SELECTOR)].filter((heading) => {
    const text = heading.textContent.trim();
    if (!text || heading.closest('[hidden]')) return false;

    if (heading.tagName === 'H2') {
      insideFaq = FAQ_PATTERN.test(text);
      if (insideFaq) heading.classList.add('is-koda-faq-heading');
      return true;
    }

    if (insideFaq) {
      heading.classList.add('is-koda-faq-question');
      return false;
    }

    return true;
  });
};

const setupToc = (article) => {
  const content = article.querySelector('[data-koda-article-content]');
  if (!content) return;

  const headings = getTocHeadings(content);

  if (article.dataset.enableToc === 'false') return;

  const toc = article.querySelector('[data-koda-toc]');
  const tocList = article.querySelector('[data-koda-toc-list]');
  const mobileToc = article.querySelector('[data-koda-mobile-toc]');
  const mobileList = article.querySelector('[data-koda-mobile-toc-list]');
  const shell = article.querySelector('[data-koda-shell]');
  const rail = article.querySelector('[data-koda-article-rail]');

  if (!tocList || !mobileList) return;

  if (headings.length < MIN_TOC_ITEMS) {
    toc?.setAttribute('hidden', '');
    mobileToc?.setAttribute('hidden', '');
    if (rail && rail.children.length <= 1) {
      rail.setAttribute('hidden', '');
      shell?.classList.add('is-without-rail');
    }
    return;
  }

  const usedIds = new Set();

  headings.forEach((heading) => {
    if (!heading.id) {
      heading.id = uniqueId(slugify(heading.textContent), usedIds);
    } else {
      usedIds.add(heading.id);
    }

    const item = document.createElement('li');
    item.append(buildTocLink(heading));
    tocList.append(item);

    const mobileItem = document.createElement('div');
    mobileItem.append(buildTocLink(heading));
    mobileList.append(mobileItem);
  });

  toc?.removeAttribute('hidden');
  mobileToc?.removeAttribute('hidden');

  const allLinks = [...article.querySelectorAll('.koda-article__toc a, .koda-article__mobile-toc-panel a')];

  const markActive = (id) => {
    allLinks.forEach((link) => {
      if (link.getAttribute('href') === `#${id}`) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible[0]) markActive(visible[0].target.id);
      },
      {
        rootMargin: '-20% 0px -65% 0px',
        threshold: [0, 1],
      }
    );

    headings.forEach((heading) => observer.observe(heading));
  }

  markActive(headings[0].id);
};

const setupMobileAccordion = (article) => {
  const trigger = article.querySelector('[data-koda-mobile-toc-trigger]');
  const panel = article.querySelector('[data-koda-mobile-toc-list]');

  if (!trigger || !panel) return;

  trigger.addEventListener('click', () => {
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  });
};

const enhanceMedia = (article) => {
  const content = article.querySelector('[data-koda-article-content]');
  if (!content) return;

  content.querySelectorAll('img').forEach((image) => {
    if (!image.hasAttribute('loading')) image.loading = 'lazy';
    if (!image.hasAttribute('decoding')) image.decoding = 'async';
  });

  content.querySelectorAll('iframe').forEach((iframe) => {
    if (!iframe.hasAttribute('loading')) iframe.loading = 'lazy';
    iframe.setAttribute('allowfullscreen', '');

    const src = iframe.getAttribute('src') || '';
    if (/youtube|youtu\.be|vimeo/.test(src)) {
      const parent = iframe.parentElement;
      if (parent && !parent.classList.contains('article-video') && parent.children.length === 1) {
        parent.classList.add('article-video');
      }
    }
  });
};

document.querySelectorAll(ARTICLE_SELECTOR).forEach((article) => {
  setupToc(article);
  setupMobileAccordion(article);
  enhanceMedia(article);
});
